/**
 * MARKETPLACE P2P (FR-U-TXN-001, 002, 012). The willing-buyer-willing-seller book.
 *
 * This replaced a 232-line fixture. The distinction it now has to carry is the one that runs through
 * the whole Money section:
 *
 *   /p2p    trade with another participant at their price. Escrow holds the seller's asset until
 *           somebody confirms. Best price if you can wait, with a counterparty risk that escrow
 *           exists to manage.
 *   /swap   buy from VASP's own float at the day's published rate. Settles immediately.
 *           Certain, now, and you pay the spread.
 *
 * ## The rule this screen is judged against
 *
 * **FR-U-TXN-012: the release path is stated before either side commits.** A seller must know
 * whether they will be asked to confirm receipt; a buyer must know whether their payment method
 * earns the automatic path. Both before money is committed, not after.
 *
 * Today NO method is one the platform settles, because every settlement adapter is a simulator
 * (ADR 0003). So every trade takes the manual path, and this screen says so plainly rather than
 * showing a badge for a control that does not exist.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LipaApiError, api, type P2P, type P2POffer } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Stagger, StaggerItem } from '@/components/motion';
import { Sheet } from '@/components/overlay';

const METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Bank transfer',
  mobile_money: 'Mobile money',
  cash_deposit: 'Cash deposit',
  lipa_wallet: 'VASP wallet',
};

export default function P2PBook() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // `?side=` so "View offer" after posting lands on the tab that contains the new offer. A seller
  // sent to the default tab would be looking at the side they did not post on.
  const [tab, setTab] = useState<'buy' | 'sell'>(params.get('side') === 'sell' ? 'sell' : 'buy');
  const { data, loading, error, reload } = useResource<P2P>(() => api.p2p('KES', tab), [tab]);

  const [taking, setTaking] = useState<P2POffer | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const offers = useMemo(() => data?.offers ?? [], [data]);

  const take = useCallback(async () => {
    if (!taking || !amount || !method) return;
    setPending(true);
    setRefusal(null);
    try {
      const result = await api.takeP2POffer(taking.id, amount, method);
      // A blocked outcome is a RESULT, not an error: a gate refused, and the participant needs to be
      // told which, in the sheet they are already looking at.
      if (result.outcome === 'blocked') {
        setRefusal(result.reason);
        return;
      }
      navigate(`/p2p/trades/${result.trade_id}`);
    } catch (e) {
      setRefusal(e instanceof LipaApiError ? e.message : 'We could not start that trade.');
    } finally {
      setPending(false);
    }
  }, [taking, amount, method, navigate]);

  if (error) return <ErrorState body="We could not load the offer book." onRetry={reload} />;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-ink">P2P trades</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            Buy and sell with other people, at their price.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Link
            to="/p2p/trades"
            className="rounded-field border border-border bg-surface px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            My trades
          </Link>
          <Link
            to="/p2p/offers/new"
            className="rounded-field border border-border bg-surface px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Post offer
          </Link>
        </div>
      </div>

      {/*
        The other product, one tap away and named for what distinguishes it. Somebody who wants
        certainty rather than the best price should not have to find the swap by accident.
      */}
      <Link
        to="/swap"
        className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-3.5 text-sm shadow-card transition hover:bg-surface-sunken"
      >
        <span>
          <span className="font-medium text-ink">In a hurry?</span>{' '}
          <span className="text-ink-muted">
            Buy from VASP at today&rsquo;s rate. Settles immediately.
          </span>
        </span>
        <span aria-hidden className="text-ink-subtle">
          &rarr;
        </span>
      </Link>

      <div
        role="tablist"
        aria-label="P2P"
        className="grid grid-cols-2 gap-1 rounded-lg bg-canvas p-1"
      >
        {(['buy', 'sell'] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`rounded-md py-2 text-sm font-medium capitalize transition ${
              tab === t ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/*
        Said once, at the top, rather than repeated on every card: it is a fact about the platform
        today, not about any particular offer.
      */}
      {data && !data.automatic_settlement_available && (
        <p className="rounded-card border border-border bg-surface-sunken p-3 text-xs text-ink-muted">
          Every trade here needs the seller to confirm they received your payment. Automatic
          settlement needs a payment rail we operate, and we do not have one yet.
        </p>
      )}

      {loading ? (
        <LoadingCard lines={4} />
      ) : offers.length === 0 ? (
        <div className="rounded-card border border-border bg-surface p-6 text-center shadow-card">
          <p className="text-sm font-medium text-ink">No offers to {tab} right now</p>
          <p className="mt-1 text-sm text-ink-muted">
            You could post your own, or buy from VASP at today&rsquo;s rate.
          </p>
        </div>
      ) : (
        <Stagger className="space-y-3">
          {offers.map((o) => (
            <StaggerItem key={o.id}>
              <article className="rounded-card border border-border bg-surface p-4 shadow-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="tabular text-sm font-semibold text-ink">
                      {o.price} {o.quote_asset}
                    </p>
                    <p className="text-xs text-ink-subtle">per {o.asset}</p>
                  </div>
                  {/* FR-U-TXN-012, on the card, before anybody taps. */}
                  <span
                    className={`rounded-pill px-2 py-0.5 text-micro font-medium ${
                      o.settles_automatically
                        ? 'bg-success/10 text-success'
                        : 'bg-surface-sunken text-ink-muted'
                    }`}
                  >
                    {o.settles_automatically ? 'Settles automatically' : 'Seller confirms'}
                  </span>
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs">
                  <div>
                    <dt className="text-ink-subtle">Available</dt>
                    <dd className="tabular font-medium text-ink">
                      {o.remaining} {o.asset}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-ink-subtle">Limit</dt>
                    <dd className="tabular font-medium text-ink">
                      {o.min} - {o.max}
                    </dd>
                  </div>
                </dl>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {o.methods.map((m) => (
                      <span
                        key={m}
                        className="rounded-pill bg-surface-sunken px-2 py-0.5 text-micro font-medium text-ink-muted"
                      >
                        {METHOD_LABEL[m] ?? m}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setTaking(o);
                      setAmount('');
                      setMethod(o.methods[0] ?? '');
                      setRefusal(null);
                    }}
                    className="shrink-0 rounded-field bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-light"
                  >
                    {tab === 'buy' ? 'Buy' : 'Sell'} {o.asset}
                  </button>
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      )}

      <Sheet open={taking !== null} onClose={() => setTaking(null)} title="Start a trade">
        {taking && (
          <div className="space-y-4">
            <div>
              <label htmlFor="p2p-amount" className="text-sm font-medium text-ink">
                Amount
              </label>
              {/*
                Decimal, in the asset's own units: "0.5" BTC. Stripping the point would send "05"
                and buy ten times what the participant typed. The server parses it with parse_money,
                which refuses more decimal places than the asset has rather than truncating them.
              */}
              <div className="mt-1 flex items-baseline gap-2">
                <input
                  id="p2p-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="0"
                  className="tabular w-full rounded-field border border-border bg-surface px-3 py-2 text-lg text-ink outline-none"
                />
                <span className="text-sm text-ink-muted">{taking.asset}</span>
              </div>
              <p className="mt-1 text-xs text-ink-subtle">
                Between {taking.min} and {taking.max}
              </p>
            </div>

            <div>
              <label htmlFor="p2p-method" className="text-sm font-medium text-ink">
                How you will pay
              </label>
              <select
                id="p2p-method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2 text-sm text-ink"
              >
                {taking.methods.map((m) => (
                  <option key={m} value={m}>
                    {METHOD_LABEL[m] ?? m}
                  </option>
                ))}
              </select>
            </div>

            {/*
              What escrow does, in the words a person would use. UF-04 asks for the protection to be
              made explicit BEFORE entering the trade room, not explained once you are inside it.
            */}
            <div className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
              <p className="font-medium text-info">How this is protected</p>
              <p className="mt-1">
                The seller&rsquo;s {taking.asset} is locked as soon as you start. They cannot spend
                it or sell it to anybody else.{' '}
                {taking.settles_automatically
                  ? 'It is released to you as soon as we see your payment arrive.'
                  : 'It is released to you when the seller confirms your payment arrived. If they do not, you can raise a dispute, and the amount stays locked until that is settled.'}
              </p>
            </div>

            {taking.terms && (
              <p className="rounded-field bg-surface-sunken p-2.5 text-xs text-ink-muted">
                <span className="font-medium text-ink">Seller&rsquo;s terms:</span> {taking.terms}
              </p>
            )}

            {refusal && (
              <div className="rounded-card border border-warning/30 bg-warning/5 p-3 text-sm text-ink">
                {refusal}
              </div>
            )}

            <button
              disabled={!amount || !method || pending}
              onClick={take}
              className="w-full rounded-field bg-primary px-4 py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              {pending ? 'Starting...' : 'Start trade'}
            </button>
          </div>
        )}
      </Sheet>
    </div>
  );
}
